import { useEffect, useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";

export interface DetailJsonEventContext<T extends object = Record<string, unknown>> {
    column: DetailJsonColumn<T>;
    data?: T;
    event: MouseEvent;
    value: ReactNode;
}

export interface DetailJsonColumn<T extends object = Record<string, unknown>> {
    className?: string;
    key?: string;
    label?: ReactNode;
    labelAlign?: "left" | "right" | "center";
    onClick?: (context: DetailJsonEventContext<T>) => void;
    onLabelClick?: (context: DetailJsonEventContext<T>) => void;
    onValueClick?: (context: DetailJsonEventContext<T>) => void;
    render?: (data?: T) => ReactNode;
    show?: boolean | (() => boolean);
    span?: number;
    value?: ReactNode | (() => ReactNode);
    width?: number;
}

export interface DetailJsonBlock<T extends object = Record<string, unknown>> {
    blockStyle?: CSSProperties;
    labelPlacement?: "left" | "top";
    list: DetailJsonColumn<T>[];
    sectionTitle?: ReactNode;
    show?: boolean | (() => boolean);
}

function resolveBoolean(value: boolean | (() => boolean) | undefined) {
    return typeof value === "function" ? value() : (value ?? true);
}

function getByPath(source: object | undefined, path: string) {
    return path.split(".").reduce<unknown>((current, key) => {
        if (!current || typeof current !== "object") return undefined;
        return (current as Record<string, unknown>)[key];
    }, source);
}

/** 依据旧 Element Plus 断点计算详情列数。 */
function useDetailColumnCount() {
    const getCount = () => (window.innerWidth >= 1920 ? 4 : window.innerWidth >= 1200 ? 3 : window.innerWidth >= 768 ? 2 : 1);
    const [count, setCount] = useState(getCount);
    useEffect(() => {
        const onResize = () => setCount(getCount());
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);
    return count;
}

/**
 * 旧 Vue detailJson 的 React 对应物。
 * 页面传字段合同，组件统一处理区块、响应式网格、label/value 布局和空值。
 */
export function DetailJson<T extends object>({
    columns,
    data,
    defaultValue = "--",
    gap = [12, 12]
}: {
    columns: DetailJsonBlock<T>[];
    data?: T;
    defaultValue?: string;
    gap?: [number, number];
}) {
    const columnCount = useDetailColumnCount();
    const resolveValue = (column: DetailJsonColumn<T>) => {
        if (column.render) return column.render(data);
        if (column.value !== undefined) return typeof column.value === "function" ? column.value() : column.value;
        const value = column.key ? getByPath(data, column.key) : undefined;
        return value == null || value === "" ? defaultValue : (value as ReactNode);
    };
    return (
        <div className="detail-json">
            {columns.filter(block => resolveBoolean(block.show)).map((block, blockIndex) => (
                <section className="detail-json__block" key={blockIndex} style={block.blockStyle}>
                    {block.sectionTitle ? (
                        <div className="detail-json__block-title">
                            {typeof block.sectionTitle === "string" ? <h2>{block.sectionTitle}</h2> : block.sectionTitle}
                        </div>
                    ) : null}
                    {block.list.length ? (
                        <div className="detail-json__grid" style={{ columnGap: gap[1], gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`, rowGap: gap[0] }}>
                            {block.list.filter(column => resolveBoolean(column.show)).map((column, index) => {
                                const value = resolveValue(column);
                                const context = (event: MouseEvent): DetailJsonEventContext<T> => ({ column, data, event, value });
                                return (
                                    <div
                                        className={`detail-json__item detail-json__item-${block.labelPlacement === "top" ? "vertical" : "horizontal"}${column.className ? ` ${column.className}` : ""}`}
                                        key={index}
                                        style={{ gridColumn: `span ${Math.min(column.span ?? 1, columnCount)}` }}
                                    >
                                        {column.label ? (
                                            <div
                                                className="detail-json__label"
                                                onClick={event => { column.onLabelClick?.(context(event)); column.onClick?.(context(event)); }}
                                                style={{ textAlign: column.labelAlign ?? (block.labelPlacement === "top" ? "left" : "right"), width: column.width }}
                                            >
                                                {column.label}
                                            </div>
                                        ) : null}
                                        <div
                                            className={`detail-json__value${column.onValueClick || column.onClick ? " is-link" : ""}`}
                                            onClick={event => { column.onValueClick?.(context(event)); column.onClick?.(context(event)); }}
                                        >
                                            {value}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}
                </section>
            ))}
        </div>
    );
}
